import { format, isValid, parseISO } from "date-fns";

export const DATE_FORMAT = "dd/MM/yyyy HH:mm";
export const DATE_TIMELINE_FORMAT = "dd/MM/yyyy";
export const DATE_PLACEHOLDER = "—";

// Formats a date to a readable string; returns DATE_PLACEHOLDER if the value is absent or invalid.
export const formatDateSafe = (
  value: string | Date | undefined | null,
  fmt = DATE_FORMAT,
): string => {
  if (!value) return DATE_PLACEHOLDER;
  const date = typeof value === "string" ? parseISO(value) : value;
  return isValid(date) ? format(date, fmt) : DATE_PLACEHOLDER;
};

// Serializes a value to indented JSON; if the value is already a JSON string, parses it first.
export const formatJson = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

// Normalizes a value into something a JSON tree can render: an object or an array.
// Returns null for primitives and for strings that are not valid JSON — those are shown as raw text.
export const toJsonData = (value: unknown): object | unknown[] | null => {
  if (value == null) return null;
  if (typeof value === "string") {
    try {
      return toJsonData(JSON.parse(value));
    } catch {
      return null;
    }
  }
  return typeof value === "object" ? (value as object) : null;
};

// Extracts the "message" or "erreur" field from details to display as a prominent banner.
export const extractProminent = (details: unknown): { text: string; isError: boolean } | null => {
  if (details == null || typeof details !== "object" || Array.isArray(details)) return null;
  const d = details as Record<string, unknown>;
  if (typeof d.erreur === "string") return { text: d.erreur, isError: true };
  if (typeof d.message === "string") return { text: d.message, isError: false };
  return null;
};

// Returns details without the "message"/"erreur" keys already shown in the prominent banner, or null if empty.
export const omitProminentKeys = (details: unknown): unknown => {
  if (details == null || typeof details !== "object" || Array.isArray(details)) return details;
  const { message: _m, erreur: _e, ...rest } = details as Record<string, unknown>;
  return Object.keys(rest).length > 0 ? rest : null;
};

export type DiffEntry = {
  key: string;
  type: "added" | "removed" | "changed";
  oldValue?: unknown;
  newValue?: unknown;
};

// Compares two values and returns the list of added, removed, or changed keys.
export const computeDiff = (prev: unknown, next: unknown): DiffEntry[] => {
  const toObj = (v: unknown) =>
    v != null && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

  const prevObj = toObj(prev);
  const nextObj = toObj(next);
  if (!prevObj && !nextObj) return [];

  const allKeys = new Set([...Object.keys(prevObj ?? {}), ...Object.keys(nextObj ?? {})]);
  const entries: DiffEntry[] = [];

  for (const key of allKeys) {
    const hadPrev = prevObj != null && key in prevObj;
    const hasNext = nextObj != null && key in nextObj;
    const oldValue = prevObj?.[key];
    const newValue = nextObj?.[key];

    if (hadPrev && !hasNext) entries.push({ key, type: "removed", oldValue });
    else if (!hadPrev && hasNext) entries.push({ key, type: "added", newValue });
    else if (JSON.stringify(oldValue) !== JSON.stringify(newValue))
      entries.push({ key, type: "changed", oldValue, newValue });
  }

  return entries;
};

// Converts a diff value to a displayable string (null → "null", object → JSON).
export const formatDiffValue = (v: unknown): string => {
  if (v == null) return "null";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
};

// Strips the "updatedAt" key from an object to reduce noise in displayed diffs.
export const stripUpdatedAt = (v: unknown): unknown => {
  if (v !== null && typeof v === "object" && !Array.isArray(v)) {
    const { updatedAt: _, ...rest } = v as Record<string, unknown>;
    return rest;
  }
  return v;
};
